import { processListingParams } from '@/api/helpers';
import { AxiosError, AxiosInstance, AxiosResponse } from 'axios';
import { getExceptionFromAxiosError } from '../helpers';
import {
  AccountLanguage,
  CompanyName,
  Country,
  Department,
  JobTitle,
  ListAccountsRequest,
  Manager,
  OfficeLocation,
} from './types';

export const accountEntitiesService = (client: AxiosInstance) => {
  const ACCOUNTS_BASE_URL = (accountId: string) => `/accounts/${accountId}`;

  const getDepartments = async (
    params: ListAccountsRequest,
    accountId: string,
  ): Promise<Department[]> => {
    try {
      const result = await client.get<
        Department[],
        AxiosResponse<Department[]>
      >(`${ACCOUNTS_BASE_URL(accountId)}/departments`, {
        withCredentials: true,
        params: processListingParams(params),
      });

      return result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const getManagers = async (
    params: ListAccountsRequest,
    accountId: string,
  ): Promise<Manager[]> => {
    try {
      const result = await client.get<Manager[], AxiosResponse<Manager[]>>(
        `${ACCOUNTS_BASE_URL(accountId)}/managers`,
        {
          withCredentials: true,
          params: processListingParams(params),
        },
      );

      return result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const getCountries = async (
    params: ListAccountsRequest,
    accountId: string,
  ): Promise<Country[]> => {
    try {
      const result = await client.get<Country[], AxiosResponse<Country[]>>(
        `${ACCOUNTS_BASE_URL(accountId)}/countries`,
        {
          withCredentials: true,
          params: processListingParams(params),
        },
      );

      return result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const getJobTitles = async (
    params: ListAccountsRequest,
    accountId: string,
  ): Promise<JobTitle[]> => {
    try {
      const result = await client.get<JobTitle[], AxiosResponse<JobTitle[]>>(
        `${ACCOUNTS_BASE_URL(accountId)}/job-titles`,
        {
          withCredentials: true,
          params: processListingParams(params),
        },
      );

      return result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const getCompanyNames = async (
    params: ListAccountsRequest,
    accountId: string,
  ): Promise<CompanyName[]> => {
    try {
      const result = await client.get<
        CompanyName[],
        AxiosResponse<CompanyName[]>
      >(`${ACCOUNTS_BASE_URL(accountId)}/company-names`, {
        withCredentials: true,
        params: processListingParams(params),
      });

      return result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const getOfficeLocations = async (
    params: ListAccountsRequest,
    accountId: string,
  ): Promise<OfficeLocation[]> => {
    try {
      const result = await client.get<
        OfficeLocation[],
        AxiosResponse<OfficeLocation[]>
      >(`${ACCOUNTS_BASE_URL(accountId)}/office-locations`, {
        withCredentials: true,
        params: processListingParams(params),
      });

      return result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const getLanguages = async (
    params: ListAccountsRequest,
    accountId: string,
  ): Promise<AccountLanguage[]> => {
    try {
      const result = await client.get<
        AccountLanguage[],
        AxiosResponse<AccountLanguage[]>
      >(`${ACCOUNTS_BASE_URL(accountId)}/languages`, {
        withCredentials: true,
        params: processListingParams(params),
      });

      return result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  return {
    getDepartments,
    getManagers,
    getCountries,
    getJobTitles,
    getCompanyNames,
    getOfficeLocations,
    getLanguages,
  };
};
